import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { BlueprintButton, BlueprintGrid, BpFrame } from "@/components/blueprint/Blueprint";
import { AppScreenshotToggle } from "@/components/shared/AppCapturePlan";
import { SEOHead } from "@/components/shared/SEOHead";
import { fetchCommunityDesigns } from "@/lib/api";
import { getDesignPreviewUrl, prefetchImages } from "@/lib/imagePrefetch";
import { APP_STORE_URL } from "@/lib/utils";
import type { CommunityDesignItem } from "@/types/api";

export const Route = createFileRoute("/create")({
  component: CreatePage,
});

const creationPaths = [
  {
    number: "01",
    title: "Start With Anything",
    body: "Out with friends? Snap the moment. Making a gift, trip shirt, team hat, party sticker, or creator drop? Chat through the idea or start from Inkwell art.",
    tags: ["Live Photo", "Gift", "Trip"],
  },
  {
    number: "02",
    title: "Clean It Up",
    body: "Crop the funny part, turn the photo into a style, combine designs, add polished word art, and compare variations until it feels like merch.",
    tags: ["Word Art", "Combine", "Variations"],
  },
  {
    number: "03",
    title: "Customize And Order",
    body: "Place the design on real products, choose colors and sizes, confirm the mockup, check out in the app, and have it shipped to you fast.",
    tags: ["Mockups", "Checkout", "Ships Fast"],
  },
];

function CreatePage() {
  const [exampleDesigns, setExampleDesigns] = useState<CommunityDesignItem[]>([]);

  useEffect(() => {
    fetchCommunityDesigns({ sort: "trending", limit: 6 })
      .then((res) => {
        setExampleDesigns(res.designs);
        prefetchImages(res.designs.map(getDesignPreviewUrl), 6);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="create-page page-enter">
      <SEOHead
        title="Create Your Own Design — Quuil"
        description="Snap a photo, transform it into custom artwork, put it on real products, and order from Quuil."
        path="/create"
        keywords={["custom design app", "photo to merch", "custom gifts", "custom team shirts", "product-ready artwork"]}
      />

      <section className="create-hero" aria-labelledby="create-heading">
        <BlueprintGrid />
        <div className="create-container create-hero-grid">
          <div className="create-copy">
            <span className="bp-kicker">Start A Design</span>
            <h1 id="create-heading">Make Something Yours</h1>
            <p>
              Snap a photo with friends, capture an inside joke, make a one-of-one gift, or build a small drop. QUUIL helps you talk through the concept, shape the design, and turn it into a real product.
            </p>
            <div className="bp-hero-actions justify-start">
              <BlueprintButton href={APP_STORE_URL}>Get The App</BlueprintButton>
              <BlueprintButton href="/gallery" variant="ghost">Browse The Inkwell</BlueprintButton>
            </div>
            <div className="bp-credit-offer bp-credit-offer-left" aria-label="Download Quuil and get 10 free credits for two design sessions">
              <span>Download Bonus</span>
              <strong>10 free credits</strong>
              <small>for 2 design sessions</small>
            </div>
          </div>

          <div className="create-phone-stage" aria-label="Quuil creation flow preview">
            <span className="create-orbit" aria-hidden="true" />
            <img
              src="/clean-screens/MAKE SOMETHING YOURS.png"
              alt="Quuil create flow screen"
              className="create-hero-phone-image"
            />
            <BpFrame className="create-phone-tag" label="FLOW">
              Create · Customize · Order
            </BpFrame>
          </div>
        </div>
      </section>

      <section className="create-section" aria-labelledby="create-path-heading">
        <BlueprintGrid light />
        <div className="create-container">
          <div className="bp-section-title">
            <span className="bp-kicker">How It Starts</span>
            <h2 id="create-path-heading">From Idea To Order</h2>
            <p>
              QUUIL is built for the whole path between “this should be on something” and “I want it delivered.” Capture, transform, edit, mock up, cart, checkout, and order history live together.
            </p>
          </div>

          <div className="create-path-grid">
            {creationPaths.map((path) => (
              <BpFrame key={path.number} className="create-path-card" label={`STEP ${path.number}`}>
                <span className="create-path-number">{path.number}</span>
                <h3>{path.title}</h3>
                <p>{path.body}</p>
                <div className="create-tags">
                  {path.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </BpFrame>
            ))}
          </div>
        </div>
      </section>

      <section className="create-section create-split-section" aria-labelledby="create-q-heading">
        <BlueprintGrid light />
        <div className="create-container create-split">
          <div className="create-mockup-stack" aria-label="Create flow UI placeholders">
            <AppScreenshotToggle
              title="Brief Builder"
              lightSrc="/clean-screens/designpartner1.png"
              darkSrc="/clean-screens/designpartner1.png"
              alt="Quuil brief builder screen"
              priority="Q"
              imageClassName="create-stack-placeholder create-stack-placeholder-a"
              placeholderClassName="create-stack-placeholder create-stack-placeholder-a"
              className="create-stack-placeholder-a"
              showModeToggle={false}
            />
            <AppScreenshotToggle
              title="Editor Canvas"
              lightSrc="/clean-screens/designpartner2.png"
              darkSrc="/clean-screens/designpartner2.png"
              alt="Quuil editor canvas screen"
              priority="Editor"
              imageClassName="create-stack-placeholder create-stack-placeholder-b"
              placeholderClassName="create-stack-placeholder create-stack-placeholder-b"
              className="create-stack-placeholder-b"
              showModeToggle={false}
            />
          </div>

          <div className="bp-section-title mb-0">
            <span className="bp-kicker">Conversation // Tools</span>
            <h2 id="create-q-heading">A Design Partner, Not A Blank Prompt Box</h2>
            <p>
              QUUIL helps you make creative decisions in conversation, then backs that up with upload tools, background removal, style effects, image editing, product-aware mockups, and saved design history.
            </p>
            <div className="create-inline-list">
              <span>Background removal</span>
              <span>Style effects</span>
              <span>Product mockups</span>
            </div>
          </div>
        </div>
      </section>

      {exampleDesigns.length > 0 && (
        <section className="create-section" aria-labelledby="examples-heading">
          <BlueprintGrid light />
          <div className="create-container">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="bp-section-title mb-0">
                <span className="bp-kicker">Inkwell Inspiration</span>
                <h2 id="examples-heading">See What Others Are Making</h2>
                <p>
                  Browse trending and new community work, save what you love, and use shared designs as a starting point for your own mockup.
                </p>
              </div>
              <Link to="/gallery" className="bp-button bp-button-ghost w-fit">
                Enter The Inkwell
              </Link>
            </div>

            <div className="create-example-grid">
              {exampleDesigns.map((design) => {
                const imgUrl = getDesignPreviewUrl(design);
                if (!imgUrl) return null;

                return (
                  <Link key={design.id} to="/gallery" className="create-example-card">
                    <img
                      src={imgUrl}
                      alt={design.title || "Community design"}
                      loading="lazy"
                      decoding="async"
                    />
                    <span>{design.title || "Inkwell Design"}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="create-final" aria-labelledby="create-cta-heading">
        <BlueprintGrid light />
        <div className="create-container text-center">
          <span className="bp-kicker">Ready</span>
          <h2 id="create-cta-heading">Open The Studio</h2>
          <p>
            Bring your own idea, borrow momentum from the Inkwell, customize the product, and order without leaving QUUIL.
          </p>
          <div className="bp-hero-actions">
            <BlueprintButton href={APP_STORE_URL}>Get Quuil Free</BlueprintButton>
            <BlueprintButton href="/showcase" variant="ghost">View App Showcase</BlueprintButton>
          </div>
        </div>
      </section>
    </div>
  );
}
